import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('ownerTenantId');
    localStorage.removeItem('ownerTenantName');
    setToken(null);
    setUser(null);
    setLoading(false);
    delete api.defaults.headers.common['Authorization'];
  };

  const bindTenantToLocalHub = async (tenantId) => {
    try {
      if (typeof window === 'undefined') return;
      const origin = window.location.origin || '';
      const host = window.location.hostname || '';
      const port = window.location.port || '';
      // Only try to bind when running inside the local hub UI
      const runningOnLocalHub = port === '9102' || host === 'localhost' || host === '127.0.0.1';
      if (!runningOnLocalHub) {
        return;
      }
      if (!tenantId) return;

      await fetch(`${origin.replace(/\/$/, '')}/local/auth/bind`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId })
      }).catch(() => {
        // best-effort only, ignore errors here
      });
    } catch {
      // ignore errors
    }
  };

  // Fetch user on mount if token exists (for page refresh)
  useEffect(() => {
    const initAuth = async () => {
      // Don't try to authenticate if we're on a public route (QR menu)
      const currentPath = window.location.pathname;
      const isPublicRoute = currentPath.startsWith('/menu/');
      
      if (isPublicRoute) {
        setLoading(false);
        return;
      }
      
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        try {
          const response = await api.get('/api/auth/me');
          const userData = response.data;
          setUser(userData);
          // Best-effort: bind tenant to local hub so sync knows which tenant to mirror
          const tenantId = userData?.tenantId || userData?.tenant_id || null;
          if (tenantId) {
            bindTenantToLocalHub(tenantId);
          }
          setToken(storedToken);
        } catch (error) {
          console.error('Failed to fetch user:', error);
          // Only logout if it's an auth error (401/403)
          if (error.response?.status === 401 || error.response?.status === 403) {
            logout();
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []); // Only run on mount

  const fetchUser = async () => {
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only logout if it's an auth error (401/403), not network errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      }
    }
  };

  const login = async (email, password, loginType) => {
    try {
      const response = await api.post('/api/auth/login', {
        email,
        password,
        loginType
      });
      
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      setLoading(false); // Set loading to false since we have user data
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      // After a successful login, bind tenant to local hub (for sync)
      const tenantId = userData?.tenantId || userData?.tenant_id || null;
      if (tenantId) {
        bindTenantToLocalHub(tenantId);
      }
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


